import { HistoryMsgType } from '@shopee/shopee-chatbot-agent-chat/dist/types';

import { getLiveChatSDKHttpHeaders } from '@/utils/liveChatToken';
import {
  ALL_TEXT,
  TO_PAY_TEXT,
  TO_RECEIVE_TEXT,
  UNPAID_TEXT,
  TO_SHIP_TEXT,
  SHIPPING_TEXT,
  COMPLETED_TEXT,
  CANCELLED_TEXT,
  SELLER_CANCELLED_TEXT,
  PARTNER_APP_BUSINESS_TRANSIFY_KEYS,
} from '@/utils/constants';
import { isDriverSupportChatbot } from './core';
import { ENTRY_POINT } from '@/types/entryPoints';
import { getHostByRegion, getSuffix } from '@/utils/getSuffix';
import { getLiveChatApiDomain } from '@/utils/saas';

// 使用方
export enum AppType {
  Chatbot,
  CsChat,
  Mitra,
  Driver,
  Partner,
  Bank,
  Food,
  ChatbotV2,
}

export enum App {
  Shopee,
  Mitra,
  Driver,
  Partner,
  Bank,
  ShopeeLite,
  Food,
}

export enum ShopeeEntryPoint {
  general,
  mePage,
  sellingPage,
  shippingPage,
  helpCentre,
  shopeeFoodHC = 14,
  RRDetailsPage = 31,
  RRShippingPage = 32,
  OrderDetailPage = 41,
  ShopeeAPPFoodVNHC = 42,
  ShopeeHomePage = 47,
  ShopeeHomePageNologin = 51,

  // premium user
  mePagePremium = 100,
  helpCentrePremium = 101,
  RRDetailsPagePremium = 104,
  RRShippingPagePremium = 105,
  OrderDetailPagePremium = 141,

  ShopeeLiteGeneral = 200,
  ShopeeLiteMePage = 201,
  ShopeeLiteHelpCentre = 202,
  ShopeeLiteShippingPage = 203,
  ShopeeLiteRRDetailsPage = 204,
  ShopeeLiteRRShippingPage = 205,
}

export enum AGENT_CHANNEL {
  Normal = 1,
  PREMIUM = 5,
  MITRA = 8,
  DRIVER = 9,
  BANK = 10,
  PARTNER = 11,
  Food = 12,
}

export enum MitraEntryPoint {
  mitra = 13,
}

export enum GrayReleaseConfigEntryPoint {
  mePage = 'mepage',
  helpCentre = 'helpcentre',
  shopeeFoodHC = 'other',
  mitra = 'mitra',
  other = 'other',
  driver = 'driver_app',
  DriverAPPFoodOrderPage = 'driver_app',
  DriverAPPHelpCenter = 'driver_app',
  DriverAPPSPXOrderPage = 'driver_app',
  BankHelpCenter = 'bank',
  BankMePage = 'bank',
  BankIDMePage = 'bank',
  BankIDHelpCenter = 'bank',
  BankSGMePage = 'bank',
  BankSGHelpCenter = 'bank',
}

export enum DriverEntryPoint {
  driver = 16,
  DriverAPPFoodOrderPage = 16,
  DriverAPPHelpCenter = 17,
  DriverAPPSPXOrderPage = 18,
  DriverAPPSPXiOrderOngoingPage = 18, // rename
  DriverAPPOngoingOrderPage = 39,
  DriverAPPFoodOrderOngoingPage = 39, // rename
  DriverAPPOrderDetailsPage = 40,
  DriverAPPFoodOrderDetailsPage = 40, // rename
  DriverAPPOrderHistoryPage = 37,
  DriverAPPFoodOrderHistoryPage = 37, // rename
  DriverAPPReportIssuePage = 38,
  DriverAPPReportFoodIssuePage = 38, // rename
  ShopeeFoodVNDriverHC = 44,
  DriverAPPSPXiOrderDetailsPage = 48,
  DriverAPPSPXiOrderHistoryPage = 49,
  DriverAPPReportSPXiIssuePage = 50,
  // https://jira.shopee.io/browse/SPCSP-52799 新增
  DriverAppSPXSamedayOrderOngoingPage = 52,
  DriverAppSPXSamedayOrderDetailPage = 53,
  DriverAppSPXSamedayOrderHistoryPage = 54,
  DriverAppSPXSamedayReportIssuePage = 55,

  SBSOrderOngoingPage = 62,
  SBSOrderDetailsPage = 63,
  SBSOrderHistoryPage = 64,
  SBSOrderReportIssuePage = 65,
}

export enum PartnerEntryPoint {
  MitraPartner = 27,
  ShopeeFoodPartner = 28,
  ShopeePayPartner = 29,
  MystockPartner = 30,
}

export enum BankEntryPoint {
  BankHelpCenter = 19,
  BankMePage = 20,
  BankIDMePage = 35,
  BankIDHelpCenter = 36,
  BankSGMePage = 45,
  BankSGHelpCenter = 46,
}

export enum FoodEntryPoint {
  ShopeeFoodVNAppHC = 43,
}

// 是否直接进线
export enum DirectlyToLiveChat {
  No = '0',
  Yes = '1',
}

export enum ShopeeLiteEntryPoint {
  ShopeeLiteGeneral = 200,
  ShopeeLiteMePage = 201,
  ShopeeLiteHelpCentre = 202,
  ShopeeLiteShippingPage = 203,
  ShopeeLiteRRDetailsPage = 204,
  ShopeeLiteRRShippingPage = 205,
}

export const NoInputRegions = { shopeeFoodHC: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }) };
export const getDriverAppNoInputRegions = (fromInUrl: string) => {
  const NoInputRegions = isDriverSupportChatbot()
    ? {
      DriverAPPHelpCenter: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }).filter(
        (item) => !['id', 'my'].includes(item),
      ),
    }[fromInUrl as unknown as string]
    : Object.values(process.env.COUNTRIES as unknown as { [key: string]: string });
  return NoInputRegions;
};

export type AppConfigType = {
  [key in App]: AppConfigItem[];
};

export interface AppConfigItem {
  app: App; // 所属app
  botTitleContent: string; // bot页面navbar文案
  greeting: string; // 欢迎语key
  liveChatKey: string; // 连接客服key
  fromInUrl:
  | keyof typeof ShopeeEntryPoint
  | keyof typeof MitraEntryPoint
  | keyof typeof DriverEntryPoint
  | keyof typeof PartnerEntryPoint
  | keyof typeof BankEntryPoint
  | keyof typeof ShopeeLiteEntryPoint
  | keyof typeof FoodEntryPoint; // from参数
  entryPoint:
  | ShopeeEntryPoint
  | MitraEntryPoint
  | DriverEntryPoint
  | PartnerEntryPoint
  | BankEntryPoint
  | ShopeeLiteEntryPoint
  | FoodEntryPoint; // 入口
  agentChatParams: {
    channel?: AGENT_CHANNEL;
  }; // 传给客服参数
  agentChatConstructorParams: {
    grayReleaseConfig?: { entry_point: any };
  }; // 传给agentChatSDK参数
  defaultMediaSdkVersion: number; // 使用media的版本号
  newMediaCountryVersionMap: Map<string, number>;
  fileUploadVersion: number; // 使用文件上传版本
  noInputRegions: string[]; // 没有input的地区
  countriesHasIntent: string[]; // 有intent的地区
  hideNewMediaCountries: string[]; // 隐藏media的地区
  historyParams: {
    axiosConfig?: {
      getHeaders?: Function;
      baseURL?: string;
    };
    app: AppType;
    extraParams: any;
  }; // 调用历史消息接口的参数
  getVideoTokenParams: any; // 获取video上传token参数
  csatParams: any; // csat参数
  liveAgentOfflineText: string; // 无客服在线key
  webFormConfig: {
    hasWebform: boolean;
    webFormKey: string;
  };
  liveDefaultEmergencyTemplate: string;
  emergencyTemplateParams: {
    channel?: AGENT_CHANNEL;
    entry_point:
    | ShopeeEntryPoint
    | MitraEntryPoint
    | DriverEntryPoint
    | PartnerEntryPoint
    | BankEntryPoint
    | ShopeeLiteEntryPoint
    | FoodEntryPoint;
  }; // emergency template 接口参数
  media: {
    bizIdForUpload?: string;
  }; // media 相关参数
  shouldCompareDMCountries: string[]; // 需要对比DM结果的地区
  hasChatbotCountries: string[]; // 有chatbot的地区
  isSupportChatbot?: boolean; // 是否支持chatbot
  isCommonHost?: boolean; // 是否使用 common set 服务，common.chatbot.{env}.shopee.{region}/xx
}

export const defaultAppConfig: AppConfigItem = {
  app: App.Shopee,
  botTitleContent: '',
  greeting: '',
  liveChatKey: '',
  fromInUrl: 'general',
  entryPoint: 0,
  agentChatParams: {},
  agentChatConstructorParams: { grayReleaseConfig: { entry_point: 'other' }},
  defaultMediaSdkVersion: 27300,
  newMediaCountryVersionMap: new Map([
    ['co-android', 27700],
    ['co-ios', 27434],
    ['cl-android', 27700],
    ['cl-ios', 27434],
  ]),
  hideNewMediaCountries: ['ar', 'es', 'fr'],
  fileUploadVersion: 26700,
  noInputRegions: process.env.NO_INPUT_REGIONS as unknown as string[],
  countriesHasIntent: process.env.COUNTRIES_HAS_INTENT as unknown as string[],
  historyParams: {
    app: AppType.Chatbot,
    axiosConfig: {
      baseURL: '',
      getHeaders: () => ({}),
    },
    extraParams: {},
  },
  getVideoTokenParams: {},
  csatParams: {},
  liveAgentOfflineText: 'live_agent_offline_text',
  webFormConfig: {
    hasWebform: true,
    webFormKey: '',
  },
  liveDefaultEmergencyTemplate: 'live_default_emergency_template',
  emergencyTemplateParams: { entry_point: ShopeeEntryPoint.general },
  media: { bizIdForUpload: '123' },
  shouldCompareDMCountries: ['sg'],
  hasChatbotCountries: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
  isSupportChatbot: true,
};
export const getConfigByEntryPoint = (entryPoint: number) => {
  switch (entryPoint) {
    case ENTRY_POINT.ShopeeAPPFoodVNHC:
    case ENTRY_POINT.ShopeeFoodVNAppHC:
    case ENTRY_POINT.ShopeeFoodVNDriverHC:
      return {
        botTitleContent: 'shopee_foodVN_bot_title',
        liveChatKey: 'shopee_foodVN_default_greeting',
        liveAgentOfflineText: 'shopee_foodVN_live_agent_offline_text',
        liveDefaultEmergencyTemplate: 'shopee_foodVN_server_error_tip',
        countriesHasIntent: [],
        shouldCompareDMCountries: [],
        historyParams: {
          app: AppType.Food,
          axiosConfig: {
            baseURL: `https://shopeefood.chatbot.${getHostByRegion(COUNTRY, 'shopee', ENVIRONMENT)}/chat`,
            getHeaders: getLiveChatSDKHttpHeaders,
          },
          extraParams: { msg_types: [HistoryMsgType.Inhouse] },
        },
        agentChatParams: { channel: AGENT_CHANNEL.Food },
        getVideoTokenParams: { channel: AGENT_CHANNEL.Food },
        csatParams: { channel: AGENT_CHANNEL.Food },
        webFormConfig: {
          hasWebform: true,
          webFormKey: '',
        },
        emergencyTemplateParams: {
          channel: AGENT_CHANNEL.Food,
          entry_point: entryPoint as unknown as ShopeeEntryPoint | DriverEntryPoint | FoodEntryPoint,
        },
        noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
        isSupportChatbot: false,
        isCommonHost: false,
        ...entryPoint === ENTRY_POINT.ShopeeFoodVNDriverHC ? {
          noInputRegions: [],
          isSupportChatbot: true,
          isCommonHost: true,
        } : {},
      };
    case ENTRY_POINT.DriverAppSPXSamedayReportIssuePage:
    case ENTRY_POINT.SBSOrderDetailsPage:
    case ENTRY_POINT.SBSOrderHistoryPage:
    case ENTRY_POINT.SBSOrderOngoingPage:
    case ENTRY_POINT.SBSOrderReportIssuePage:
      return {
        isSuportChatbot: false,
        hasChatbotCountries: [],
        noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
      };
    case ENTRY_POINT.DriverAppSPXSamedayOrderOngoingPage:
    case ENTRY_POINT.DriverAppSPXSamedayOrderDetailPage:
    case ENTRY_POINT.DriverAppSPXSamedayOrderHistoryPage:
      return {
        isSuportChatbot: true,
        hasChatbotCountries: ['id'],
      };
    case ENTRY_POINT.DriverAPPReportSPXiIssuePage:
      return {
        isSuportChatbot: true,
        hasChatbotCountries: ['my', 'th'],
      };
    case ENTRY_POINT.DriverAPPSPXOrderPage:
    case ENTRY_POINT.DriverAPPSPXiOrderHistoryPage:
    case ENTRY_POINT.DriverAPPSPXiOrderDetailsPage:
      return {
        isSuportChatbot: true,
        hasChatbotCountries: ['my', 'th', 'id'],
      };
    default:
      return {};
  }
};

function generateAppConfig(): AppConfigType {
  return {
    [App.Shopee]: Object.keys(ShopeeEntryPoint).map((fromInUrl: string) => ({
      ...defaultAppConfig,
      webFormConfig: {
        hasWebform: !['ph'].includes(process.env.country as string),
        webFormKey: '',
      },
      fromInUrl: fromInUrl as keyof typeof ShopeeEntryPoint,
      entryPoint: ShopeeEntryPoint[fromInUrl as keyof typeof ShopeeEntryPoint],
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      noInputRegions: NoInputRegions[fromInUrl as keyof typeof NoInputRegions] || process.env.NO_INPUT_REGIONS,
      emergencyTemplateParams: { entry_point: ShopeeEntryPoint[fromInUrl as keyof typeof ShopeeEntryPoint] },
      ...getConfigByEntryPoint(ShopeeEntryPoint[fromInUrl as keyof typeof ShopeeEntryPoint]),
    })),
    [App.Mitra]: Object.keys(MitraEntryPoint).map((fromInUrl: string) => ({
      app: App.Mitra,
      botTitleContent: '',
      greeting: 'mitra_greeting',
      liveChatKey: 'mitra_msg_live',
      fromInUrl: fromInUrl as keyof typeof MitraEntryPoint,
      entryPoint: MitraEntryPoint[fromInUrl as keyof typeof MitraEntryPoint],
      agentChatParams: { channel: AGENT_CHANNEL.MITRA },
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      defaultMediaSdkVersion: 0,
      newMediaCountryVersionMap: new Map([]),
      fileUploadVersion: 0,
      hideNewMediaCountries: [],
      // 对于mitra，全部地区都隐藏
      noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
      countriesHasIntent: [],
      historyParams: {
        app: AppType.Mitra,
        axiosConfig: {
          baseURL: '/chat',
          getHeaders: getLiveChatSDKHttpHeaders,
        },
        extraParams: { msg_types: [HistoryMsgType.Inhouse] },
      },
      getVideoTokenParams: { channel: AGENT_CHANNEL.MITRA },
      csatParams: { channel: AGENT_CHANNEL.MITRA },
      liveAgentOfflineText: 'mitra_live_agent_offline_text',
      webFormConfig: {
        hasWebform: false,
        webFormKey: '',
      },
      liveDefaultEmergencyTemplate: 'chat_template_mitra',
      emergencyTemplateParams: {
        channel: AGENT_CHANNEL.MITRA,
        entry_point: MitraEntryPoint[fromInUrl as keyof typeof MitraEntryPoint],
      },
      media: { bizIdForUpload: '131' },
      shouldCompareDMCountries: [],
      hasChatbotCountries: [],
      isSupportChatbot: false,
    })),
    [App.Driver]: Object.keys(DriverEntryPoint).map((fromInUrl: string) => ({
      app: App.Driver,
      botTitleContent: '',
      greeting: 'default_greeting',
      liveChatKey: '',
      fromInUrl: fromInUrl as keyof typeof DriverEntryPoint,
      entryPoint: DriverEntryPoint[fromInUrl as keyof typeof DriverEntryPoint],
      agentChatParams: { channel: AGENT_CHANNEL.DRIVER },
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      defaultMediaSdkVersion: 0,
      newMediaCountryVersionMap: new Map([]),
      fileUploadVersion: 0,
      hideNewMediaCountries: [],
      // 对于driver，全部地区都隐藏
      // noInputRegions: Object.values((process.env.COUNTRIES as unknown) as { [key: string]: string }),
      noInputRegions: getDriverAppNoInputRegions(fromInUrl) || [],
      countriesHasIntent: [],
      historyParams: {
        app: AppType.Driver,
        axiosConfig: {
          baseURL: '/chat',
          getHeaders: getLiveChatSDKHttpHeaders,
        },
        extraParams: { msg_types: [HistoryMsgType.Inhouse] },
      },
      getVideoTokenParams: { channel: AGENT_CHANNEL.DRIVER },
      csatParams: { channel: AGENT_CHANNEL.DRIVER },
      liveAgentOfflineText: 'live_agent_offline_text',
      webFormConfig: {
        hasWebform: true,
        webFormKey: 'live_offline_form_page_for_driver',
      },
      liveDefaultEmergencyTemplate: 'live_default_emergency_template',
      emergencyTemplateParams: {
        channel: AGENT_CHANNEL.DRIVER,
        entry_point: DriverEntryPoint[fromInUrl as keyof typeof DriverEntryPoint],
      },
      media: { bizIdForUpload: '123' },
      shouldCompareDMCountries: [],
      hasChatbotCountries: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
      isSupportChatbot: isDriverSupportChatbot(),
      ...getConfigByEntryPoint(DriverEntryPoint[fromInUrl as keyof typeof DriverEntryPoint]),
    })),
    [App.Partner]: Object.keys(PartnerEntryPoint).map((fromInUrl: string) => ({
      app: App.Partner,
      botTitleContent: '',
      greeting: '',
      liveChatKey: '',
      fromInUrl: fromInUrl as keyof typeof PartnerEntryPoint,
      entryPoint: PartnerEntryPoint[fromInUrl as keyof typeof PartnerEntryPoint],
      agentChatParams: { channel: AGENT_CHANNEL.PARTNER },
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      defaultMediaSdkVersion: 0,
      newMediaCountryVersionMap: new Map([]),
      fileUploadVersion: 0,
      hideNewMediaCountries: [],
      noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
      countriesHasIntent: [],
      historyParams: {
        app: AppType.Partner,
        axiosConfig: {
          baseURL: '/chat',
          getHeaders: getLiveChatSDKHttpHeaders,
        },
        extraParams: { msg_types: [HistoryMsgType.Inhouse] },
      },
      getVideoTokenParams: { channel: AGENT_CHANNEL.PARTNER },
      csatParams: { channel: AGENT_CHANNEL.PARTNER },
      liveAgentOfflineText: PARTNER_APP_BUSINESS_TRANSIFY_KEYS[fromInUrl as keyof typeof PartnerEntryPoint]?.noAgent,
      webFormConfig: {
        hasWebform: true,
        webFormKey: 'live_offline_form_page_for_partner',
      },
      liveDefaultEmergencyTemplate:
        PARTNER_APP_BUSINESS_TRANSIFY_KEYS[fromInUrl as keyof typeof PartnerEntryPoint]?.emergency,
      emergencyTemplateParams: {
        channel: AGENT_CHANNEL.PARTNER,
        entry_point: PartnerEntryPoint[fromInUrl as keyof typeof PartnerEntryPoint],
      },
      media: { bizIdForUpload: '123' },
      shouldCompareDMCountries: [],
      hasChatbotCountries: [],
      isSupportChatbot: false,
    })),
    [App.Bank]: Object.keys(BankEntryPoint).map((fromInUrl: string) => ({
      app: App.Bank,
      botTitleContent: 'bank_page_head_title',
      greeting: 'default_greeting',
      liveChatKey: 'bank_msg_live',
      fromInUrl: fromInUrl as keyof typeof BankEntryPoint,
      entryPoint: BankEntryPoint[fromInUrl as keyof typeof BankEntryPoint],
      agentChatParams: { channel: AGENT_CHANNEL.BANK },
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      defaultMediaSdkVersion: 0,
      newMediaCountryVersionMap: new Map([]),
      fileUploadVersion: 0,
      // 对于bank，全部地区都隐藏
      // hideNewMediaCountries: Object.values((process.env.COUNTRIES as unknown) as { [key: string]: string }),
      hideNewMediaCountries: [],
      // 对于bank，全部地区都隐藏
      noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }).filter(
        item => !['id', 'ph'].includes(item),
      ),
      countriesHasIntent: [],
      historyParams: {
        app: AppType.Bank,
        axiosConfig: {
          baseURL: '/chat',
          getHeaders: getLiveChatSDKHttpHeaders,
        },
        extraParams: { msg_types: [HistoryMsgType.Inhouse] },
      },
      getVideoTokenParams: { channel: AGENT_CHANNEL.BANK },
      csatParams: { channel: AGENT_CHANNEL.BANK },
      liveAgentOfflineText: 'bank_live_agent_offline_text',
      // bank id才支持webform
      webFormConfig: {
        hasWebform: fromInUrl === 'BankIDMePage' || fromInUrl === 'BankIDHelpCenter',
        webFormKey:
          fromInUrl === 'BankIDMePage' || fromInUrl === 'BankIDHelpCenter' ? 'live_offline_form_page_for_bank' : '',
      },
      liveDefaultEmergencyTemplate: 'chat_template_bank',
      emergencyTemplateParams: {
        channel: AGENT_CHANNEL.BANK,
        entry_point: BankEntryPoint[fromInUrl as keyof typeof BankEntryPoint],
      },
      media: { bizIdForUpload: '123' },
      shouldCompareDMCountries: [],
      hasChatbotCountries: ['id', 'ph'],
      isSupportChatbot: true,
    })),
    [App.ShopeeLite]: Object.keys(ShopeeLiteEntryPoint).map((fromInUrl: string) => ({
      ...defaultAppConfig,
      app: App.ShopeeLite,
      webFormConfig: {
        hasWebform: !['ph'].includes(process.env.country as string),
        webFormKey: '',
      },
      fromInUrl: fromInUrl as keyof typeof ShopeeLiteEntryPoint,
      entryPoint: ShopeeLiteEntryPoint[fromInUrl as keyof typeof ShopeeLiteEntryPoint],
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      noInputRegions: NoInputRegions[fromInUrl as keyof typeof NoInputRegions] || process.env.NO_INPUT_REGIONS,
      emergencyTemplateParams: { entry_point: ShopeeLiteEntryPoint[fromInUrl as keyof typeof ShopeeLiteEntryPoint] },
    })),
    [App.Food]: Object.keys(FoodEntryPoint).map((fromInUrl: string) => ({
      app: App.Food,
      botTitleContent: '',
      greeting: '',
      liveChatKey: '',
      fromInUrl: fromInUrl as keyof typeof FoodEntryPoint,
      entryPoint: FoodEntryPoint[fromInUrl as keyof typeof FoodEntryPoint],
      agentChatParams: { channel: AGENT_CHANNEL.Food },
      agentChatConstructorParams: { grayReleaseConfig: { entry_point: GrayReleaseConfigEntryPoint[fromInUrl as keyof typeof GrayReleaseConfigEntryPoint] || 'other' }},
      defaultMediaSdkVersion: 0,
      newMediaCountryVersionMap: new Map([]),
      fileUploadVersion: 0,
      // 对于bank，全部地区都隐藏
      // hideNewMediaCountries: Object.values((process.env.COUNTRIES as unknown) as { [key: string]: string }),
      hideNewMediaCountries: [],
      // 对于bank，全部地区都隐藏
      noInputRegions: Object.values(process.env.COUNTRIES as unknown as { [key: string]: string }),
      countriesHasIntent: [],
      historyParams: {
        app: AppType.Food,
        axiosConfig: {
          baseURL: '/chat',
          getHeaders: getLiveChatSDKHttpHeaders,
        },
        extraParams: { msg_types: [HistoryMsgType.Inhouse] },
      },
      getVideoTokenParams: { channel: AGENT_CHANNEL.Food },
      csatParams: { channel: AGENT_CHANNEL.Food },
      liveAgentOfflineText: 'live_agent_offline_text',
      webFormConfig: {
        hasWebform: true,
        webFormKey: '',
      },
      liveDefaultEmergencyTemplate: 'live_default_emergency_template',
      emergencyTemplateParams: {
        channel: AGENT_CHANNEL.Food,
        entry_point: FoodEntryPoint[fromInUrl as keyof typeof FoodEntryPoint],
      },
      media: { bizIdForUpload: '123' },
      shouldCompareDMCountries: [],
      isSupportChatbot: false,
      hasChatbotCountries: [],
      ...getConfigByEntryPoint(FoodEntryPoint[fromInUrl as keyof typeof FoodEntryPoint]),
    })),
  };
}

export let AppConfig: AppConfigType = generateAppConfig();

// https://jira.shopee.io/browse/SPCSP-54998
// 从FloatingBubble返回到聊天时，
// 由于url缺失search部分，此时生成的AppConfig不是正确的，
// 因此需要在从storage同步search之后，重新生成一次AppConfig
// FIXME: 实际上在同步search之前，已经可能出现因为配置导致执行了其他分支的情况。烦请熟悉项目逻辑的开发留意下
export function updateAppConfig() {
  AppConfig = generateAppConfig();
}

export const useLiveChatSdkAppList = [App.Mitra, App.Driver, App.Partner, App.Bank, App.Food];

export const useDefaultABTestConfigList = [App.Mitra, App.Driver, App.Partner, App.Bank];

export enum SPEX_ORDER_LIST_TYPE {
  ALL_NEW = 0,
  PAID = 1,
  UNPAID = 2,
  COMPLETED = 3,
  CANCELLED = 4,
  SHIPPED = 5,
  ALL = 6,
  TOSHIP = 7,
  TORECEIVE = 8,
  TOPAY = 9,
  TOSHIP_UNPROCESSED = 10,
  TOSHIP_PROCESSED = 11,
  RETURN = 12,
  GROUP_BUY_ALL = 13,
  GROUP_BUY_ONGOING = 14,
  GROUP_BUY_COMPLETED = 15,
  GROUP_BUY_INVALID = 16,
}

export const ORDER_LIST_TYPE_TO_TEXT = {
  [SPEX_ORDER_LIST_TYPE.ALL_NEW]: ALL_TEXT,
  [SPEX_ORDER_LIST_TYPE.ALL]: ALL_TEXT,
  [SPEX_ORDER_LIST_TYPE.TOPAY]: TO_PAY_TEXT,
  [SPEX_ORDER_LIST_TYPE.TOSHIP]: TO_SHIP_TEXT,
  [SPEX_ORDER_LIST_TYPE.TORECEIVE]: TO_RECEIVE_TEXT,
  [SPEX_ORDER_LIST_TYPE.COMPLETED]: COMPLETED_TEXT,
  [SPEX_ORDER_LIST_TYPE.CANCELLED]: CANCELLED_TEXT,
  [SPEX_ORDER_LIST_TYPE.UNPAID]: UNPAID_TEXT,
  [SPEX_ORDER_LIST_TYPE.SHIPPED]: SHIPPING_TEXT,
};
